import type {
  FirstMessageTypedEvent,
  WidgetInputFocusedEvent,
  WidgetLoadedEvent,
  WidgetState,
  WidgetTransitionSource,
  WidgetViewChangedEvent,
} from '@/domain/analytics';
import { ACTION_TYPES, SYSTEM_INITIATOR, UNKNOWN_CUSTOMER_INITIATOR } from '@/domain/analytics';
import type { ServerConfiguration } from '@/domain/configuration';
import type { DeviceConfig } from '@/domain/deviceConfig';
import type { GoogleAnalyticsGateway } from '@/domain/gateway';
import { isLegacyUIEnabled } from '@/domain/legacyUiConfig';
import AnalyticsEventGateway from '@/gateways/AnalyticsEventGateway';
import { getDeviceType, newAnalyticsEvent } from '@/lib/analytics';
import { getAnalyticsPageUrl } from '@/lib/analyticsPageUrl';
import { getAnalyticsEndpoint } from '@/lib/getAnalyticsEndpoint';
import { getWidgetViewChangedGoogleAnalyticsEvent } from '@/lib/googleAnalytics';

export interface BootstrapAnalytics {
  hasTrackedWidgetLoaded(): boolean;
  trackFirstMessageTyped(widgetState: WidgetState): void;
  trackWidgetInputFocused(widgetState: WidgetState): void;
  trackWidgetLoaded(widgetState: WidgetState): void;
  trackWidgetViewChanged(fromState: WidgetState, toState: WidgetState, source?: WidgetTransitionSource): void;
  destroy(): void;
}

type SharedField = 'appId' | 'clientId' | 'deviceType' | 'gladlyCustomerId' | 'legacyUI' | 'pageUrl' | 'platform';

/** The bootstrap's events, less the fields every one of them shares. */
type BootstrapEventFields =
  | Omit<FirstMessageTypedEvent, SharedField>
  | Omit<WidgetInputFocusedEvent, SharedField>
  | Omit<WidgetLoadedEvent, SharedField>
  | Omit<WidgetViewChangedEvent, SharedField>;

export function createBootstrapAnalytics({
  appId,
  clientId,
  serverConfig,
  deviceConfig,
  gladlyCustomerId,
  googleAnalyticsGateway,
}: {
  appId: string;
  clientId: string;
  serverConfig: ServerConfiguration;
  deviceConfig: DeviceConfig;
  gladlyCustomerId?: string;
  googleAnalyticsGateway: GoogleAnalyticsGateway;
}): BootstrapAnalytics {
  const gateway = new AnalyticsEventGateway({
    endpoint: getAnalyticsEndpoint(serverConfig),
    orgId: serverConfig.orgId,
  });

  const legacyUI = isLegacyUIEnabled(serverConfig);

  let hasFired = false;

  const publish = (fields: BootstrapEventFields): void => {
    gateway.publish(
      newAnalyticsEvent(serverConfig.orgId, {
        ...fields,
        appId,
        clientId,
        deviceType: getDeviceType(deviceConfig.platform),
        gladlyCustomerId,
        legacyUI,
        pageUrl: getAnalyticsPageUrl(),
        platform: deviceConfig.platform,
      }),
    );
  };

  return {
    hasTrackedWidgetLoaded(): boolean {
      return hasFired;
    },

    trackFirstMessageTyped(widgetState: WidgetState): void {
      publish({ action: ACTION_TYPES.FIRST_MESSAGE_TYPED, initiator: UNKNOWN_CUSTOMER_INITIATOR, widgetState });
    },

    trackWidgetInputFocused(widgetState: WidgetState): void {
      publish({ action: ACTION_TYPES.WIDGET_INPUT_FOCUSED, initiator: UNKNOWN_CUSTOMER_INITIATOR, widgetState });
    },

    trackWidgetLoaded(widgetState: WidgetState): void {
      if (hasFired) {
        return;
      }
      hasFired = true;

      publish({ action: ACTION_TYPES.WIDGET_LOADED, initiator: SYSTEM_INITIATOR, widgetState });
    },

    trackWidgetViewChanged(fromState: WidgetState, toState: WidgetState, source?: WidgetTransitionSource): void {
      publish({
        action: ACTION_TYPES.WIDGET_VIEW_CHANGED,
        fromState,
        initiator: UNKNOWN_CUSTOMER_INITIATOR,
        toState,
        widgetState: toState,
      });

      const gaEvent = getWidgetViewChangedGoogleAnalyticsEvent(fromState, toState, source);
      if (gaEvent) {
        googleAnalyticsGateway.sendEvent(gaEvent.eventName, gaEvent.parameters);
      }
    },

    destroy(): void {
      gateway.destroy();
    },
  };
}
